import {
  SaveUserWorldCupPredictionRequest,
  WorldCupPredictionGroups,
  WorldCupPredictionGroupTeam,
  WorldCupPredictionPosition,
  WorldCupPredictionRound,
  WorldCupPredictionTeam,
  WorldCupPredictionThirdPlace,
} from '@wc26-forecast/api/types';
import { resolveWorldCupMatchSourceTeamId } from '@wc26-forecast/model/lib';
import type {
  WorldCupForecastState,
  WorldCupMatch,
  WorldCupMatchId,
  WorldCupRoundType,
  WorldCupTeamId,
} from '@wc26-forecast/model/types';

export function mapWorldCupStateToUserPrediction(state: WorldCupForecastState): SaveUserWorldCupPredictionRequest {
  const getTeam = (teamId: WorldCupTeamId | null | undefined): WorldCupPredictionTeam | null => {
    if (!teamId) {
      return null;
    }
    const team = state.teams.byId[teamId];
    if (!team) {
      return null;
    }
    return {
      icon: `${team.flag}.svg`,
      name: team.code,
    };
  };
  const getSelectedTeam = (teamId: WorldCupTeamId | null | undefined, isSelected?: boolean): WorldCupPredictionTeam | null => {
    const team = getTeam(teamId);
    if (!team) {
      return null;
    }
    return {
      ...team,
      ...(isSelected ? { isSelected: true } : {}),
    };
  };
  const getRoundMatchIds = (round: WorldCupRoundType): WorldCupMatchId[] => {
    const roundMatches = state.matchesByRound[round];
    if (!roundMatches) {
      return [];
    }
    return [
      ...roundMatches.left,
      ...roundMatches.right,
    ];
  };
  const mapRound = (round: WorldCupRoundType): WorldCupPredictionRound => {
    const result: WorldCupPredictionRound = {};
    getRoundMatchIds(round)
      .forEach((matchId, index) => {
        const match = state.matches.byId[matchId];
        if (!match) {
          return;
        }
        const matchResult = state.prediction.matchResults[matchId];
        const place1TeamId = resolveWorldCupMatchSourceTeamId(match.homeSource, state);
        const place2TeamId = resolveWorldCupMatchSourceTeamId(match.awaySource, state);
        const place1 = getSelectedTeam(
          place1TeamId,
          matchResult?.winnerTeamId === place1TeamId,
        );
        const place2 = getSelectedTeam(
          place2TeamId,
          matchResult?.winnerTeamId === place2TeamId,
        );
        if (!place1 || !place2) {
          return;
        }
        result[index + 1] = {
          place1,
          place2,
        };
      });
    return result;
  };
  const getSingleMatchByRound = (round: WorldCupRoundType): WorldCupMatch | null => {
    const [ matchId ] = getRoundMatchIds(round);
    return matchId ? state.matches.byId[matchId] ?? null : null;
  };
  const groups: WorldCupPredictionGroups = {};
  state.groups.ids.forEach((groupId) => {
    const placements = state.prediction.groupPlacements[groupId];
    if (!placements) {
      return;
    }
    groups[groupId] = ([ 1, 2, 3, 4 ] as WorldCupPredictionPosition[])
      .map((position) => {
        const team = getTeam(placements[position]);
        if (!team) {
          return null;
        }
        return {
          ...team,
          position,
        };
      })
      .filter((team): team is WorldCupPredictionGroupTeam => Boolean(team));
  });
  const thirdPlace: WorldCupPredictionThirdPlace = {};
  state.groups.ids
    .forEach((groupId) => {
      const thirdPlaceTeamId = state.prediction.groupPlacements[groupId]?.[3]!;
      const isSelected = Object.values(state.prediction.bestThirdTeams)
        .includes(thirdPlaceTeamId);
      const team = getSelectedTeam(thirdPlaceTeamId, isSelected);
      if (team) {
        thirdPlace[groupId] = team;
      }
    });
  const thirdPlaceMatch = getSingleMatchByRound('THIRD_PLACE');
  const thirdPlaceResult = thirdPlaceMatch
    ? state.prediction.matchResults[thirdPlaceMatch.id]
    : null;
  const thirdPlaceHomeTeamId = thirdPlaceMatch
    ? resolveWorldCupMatchSourceTeamId(thirdPlaceMatch.homeSource, state)
    : null;
  const thirdPlaceAwayTeamId = thirdPlaceMatch
    ? resolveWorldCupMatchSourceTeamId(thirdPlaceMatch.awaySource, state)
    : null;
  const finalMatch = getSingleMatchByRound('FINAL');
  const finalHomeTeam = finalMatch
    ? getTeam(resolveWorldCupMatchSourceTeamId(finalMatch.homeSource, state))
    : null;
  const finalAwayTeam = finalMatch
    ? getTeam(resolveWorldCupMatchSourceTeamId(finalMatch.awaySource, state))
    : null;
  return {
    events: {
      final: {
        team1: finalHomeTeam ?? { icon: '', name: '' },
        team2: finalAwayTeam ?? { icon: '', name: '' },
        result1: state.prediction.finalScorePrediction?.homeScore ?? 0,
        result2: state.prediction.finalScorePrediction?.awayScore ?? 0,
      },
      groups,
      final_1_16: mapRound('ROUND_OF_32'),
      final_1_8: mapRound('ROUND_OF_16'),
      final_1_4: mapRound('QUARTER_FINAL'),
      final_1_2: mapRound('SEMI_FINAL'),
      third_place_match: {
        team1: getSelectedTeam(
          thirdPlaceHomeTeamId,
          thirdPlaceResult?.winnerTeamId === thirdPlaceHomeTeamId,
        ) ?? { icon: '', name: '' },
        team2: getSelectedTeam(
          thirdPlaceAwayTeamId,
          thirdPlaceResult?.winnerTeamId === thirdPlaceAwayTeamId,
        ) ?? { icon: '', name: '' },
      },
      thirdPlace,
    },
  };
}
